import { BUILDER_ENABLED, BUILDER_FEE } from "#/hooks/builder-fee";

/** Slippage tolerance when force-closing positions (wider, to ensure fill). */
export const MARKET_CLOSE_SLIPPAGE_PCT = 0.05;

/** Slippage tolerance for ordinary market orders from the order form. */
export const MARKET_ORDER_SLIPPAGE_PCT = 0.01;

/** Minimum notional size (USD) accepted by Hyperliquid. */
export const MIN_ORDER_NOTIONAL_USD = 10;

/** Worst-case (tier-0) Hyperliquid taker fee, as a fraction of notional. */
const TAKER_FEE_RATE = 0.00045;

/**
 * Per-notional cost reserved when sizing an order at max buying power:
 * taker + builder fee paid on fill, plus headroom for the bid/ask spread and
 * mark drift between the `activeAssetData` snapshot and matching. HL's
 * `availableToTrade`/`maxTradeSzs` are the EXACT zero-headroom margin boundary
 * (verified against the API: maxTradeSz == availableToTrade × leverage ÷ mark),
 * so without this reserve a 100% order is rejected ("Insufficient margin to
 * place order") the moment fees/spread land. Costs are per-notional, so as a
 * fraction of margin they scale with leverage — a flat haircut can't work.
 * ponytail: flat 5bp drift headroom; make it spread-aware (bestAsk−mark) if
 * illiquid-market 100% orders still bounce.
 */
export const MAX_SIZE_COST_RATE =
	TAKER_FEE_RATE + (BUILDER_ENABLED ? BUILDER_FEE / 100_000 : 0) + 0.0005;

/** Default TWAP duration in minutes when none supplied. */
export const DEFAULT_TWAP_DURATION_MIN = 30;

/**
 * Age after which the latest trade is considered stale for *display* purposes
 * (orderbook center value + tab title). Past this, an illiquid market's last
 * trade can sit far from the live mark, so we show mark instead. Tunable.
 */
export const STALE_TRADE_PRICE_AGE_MS = 60_000;

export function computeMarketSlippagePrice(
	markPrice: number,
	isSell: boolean,
	slippagePct: number,
): string {
	const factor = isSell ? 1 - slippagePct : 1 + slippagePct;
	return Number((markPrice * factor).toPrecision(5)).toString();
}

/** Strip a leading minus sign from a signed size string without losing precision. */
export function absSizeStr(szi: string | number | null | undefined): string {
	const raw = String(szi ?? "0");
	return raw.startsWith("-") ? raw.slice(1) : raw;
}
